import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/contexts/NotificationContext';
import { X, ChevronRight, Package, Car, Bell } from 'lucide-react';

const ICONS = {
  new_delivery: Package,
  ride_request: Car,
  default: Bell
};

export default function NotificationBanner() {
  const navigate = useNavigate();
  const { notifications, dismissNotification, markAsRead } = useNotifications();
  
  // Only show unread, non-dismissed notifications.
  // Job-offer notifications (new_delivery) are rendered by DriverJobOfferSheet
  // as a bottom sheet with miles + ETA + Accept/Decline — so we skip them here
  // to avoid double-rendering. NotificationBanner stays focused on lightweight
  // top-of-screen toasts for everything else.
  const activeNotifications = notifications
    .filter(n => !n.read && n.type !== 'new_delivery')
    .slice(0, 3);

  if (activeNotifications.length === 0) return null;

  const handleTap = (notif) => {
    markAsRead(notif.id);
    
    if (notif.type === 'new_delivery' && notif.data?.id) {
      navigate('/driver/earnings');
    }
  };

  const handleDismiss = (e, id) => {
    e.stopPropagation();
    dismissNotification(id);
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 p-2 pointer-events-none">
      <div className="max-w-lg mx-auto space-y-2">
        {activeNotifications.map((notif, index) => {
          const Icon = ICONS[notif.type] || ICONS.default;
          
          return (
            <div
              key={notif.id}
              onClick={() => handleTap(notif)}
              className="pointer-events-auto bg-[#121212]/95 backdrop-blur border border-white/10 rounded-xl p-3 shadow-2xl cursor-pointer transform transition-all duration-300 animate-slide-down"
              style={{ 
                animationDelay: `${index * 100}ms`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
              }}
              data-testid={`notification-${notif.id}`}
            >
              <div className="flex items-center gap-3">
                {/* Icon */}
                <div className="p-2 rounded-lg bg-[#0047FF]/20 shrink-0">
                  <Icon className="w-5 h-5 text-[#4D88FF]" />
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">
                    {notif.title}
                  </p>
                  <p className="text-xs text-zinc-400 truncate">
                    {notif.message}
                  </p>
                  {notif.subtitle && (
                    <p className="text-xs text-emerald-400 font-medium">
                      {notif.subtitle}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <ChevronRight className="w-4 h-4 text-zinc-500" />
                  <button
                    onClick={(e) => handleDismiss(e, notif.id)}
                    className="p-1.5 rounded-full hover:bg-zinc-700 text-zinc-500 hover:text-white transition-colors"
                    data-testid={`dismiss-${notif.id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      <style>{`
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
